// hooks/useDataFetching.js
import { ref, computed } from 'vue';
import { useStore } from 'vuex';

const OFFICIAL_API_PROVIDERS = ['whatsapp_cloud', 'whatsapp_coexistence'];

export function useDataFetching() {
  const store = useStore();

  // State refs (igual à versão original)
  const tables = ref([]);
  const phoneColumns = ref([]);
  const nameColumns = ref([]);
  const allColumns = ref([]);
  const columns = ref([]);
  const tableData = ref([]);
  const totalCount = ref(0);
  const generatedSql = ref(null); // ✅ NOVO: SQL gerada
  const isTableLoading = ref(false);
  const whatsappMessageLimit = ref({});
  const isFetchingWhatsappLimit = ref(false);
  const whatsappLimitError = ref(null);

  // Computed options (exatamente como na versão original)
  const tableOptions = computed(() => {
    return tables.value.map(table => ({ label: table, value: table }));
  });

  const phoneColumnOptions = computed(() => {
    // ✅ PROTEÇÃO: Sempre retornar array válido
    if (!Array.isArray(phoneColumns.value)) {
      console.warn(
        '⚠️ [phoneColumnOptions] phoneColumns.value não é array, retornando []'
      );
      return [];
    }

    return phoneColumns.value.map(column => ({
      label: column.column_name || column,
      value: column.column_name || column,
    }));
  });

  const nameColumnOptions = computed(() => {
    // ✅ PROTEÇÃO: Sempre retornar array válido
    if (!Array.isArray(nameColumns.value)) {
      console.warn(
        '⚠️ [nameColumnOptions] nameColumns.value não é array, retornando []'
      );
      return [];
    }

    return nameColumns.value.map(column => ({
      label: column.column_name || column,
      value: column.column_name || column,
    }));
  });

  const allColumnOptions = computed(() => {
    // ✅ PROTEÇÃO: Sempre retornar array válido
    if (!Array.isArray(allColumns.value)) {
      console.warn(
        '⚠️ [allColumnOptions] allColumns.value não é array, retornando []'
      );
      return [];
    }

    return allColumns.value.map(column => ({
      label: column.column_name || column,
      value: column.column_name || column,
    }));
  });

  const columnOptions = computed(() =>
    columns.value.map(column => ({
      label: column.column_name,
      value: column.column_name,
    }))
  );

  const fetchTables = async () => {
    try {
      await store.dispatch('externalDatabaseConnection/listTables');
      const fetchedTables =
        store.getters['externalDatabaseConnection/getTables'];
      tables.value = Array.isArray(fetchedTables) ? fetchedTables : [];
    } catch (error) {
      console.error('❌ [fetchTables] Erro ao buscar tabelas:', error);
      tables.value = [];
    }
  };

  const fetchTableColumns = async tableName => {
    if (!tableName) {
      console.warn('⚠️ [fetchTableColumns] Nome da tabela não fornecido');
      return;
    }

    // Verificar se já está em cache
    const cachedColumns =
      store.getters['campaigns/getCachedTableColumns'](tableName);
    if (cachedColumns) {
      // ATRIBUIÇÃO DIRETA (como na versão original)
      phoneColumns.value = cachedColumns.phoneColumns;
      nameColumns.value = cachedColumns.nameColumns;
      allColumns.value = cachedColumns.allColumns;
      return;
    }

    try {
      const [phoneColumnsData, nameColumnsData] = await Promise.all([
        store.dispatch('externalDatabaseConnection/describeTablePhoneColumns', {
          tableName,
        }),
        store.dispatch('externalDatabaseConnection/describeTableNameColumns', {
          tableName,
        }),
      ]);
      await store.dispatch('externalDatabaseConnection/describeTable', {
        tableName,
      });
      const description =
        store.getters['externalDatabaseConnection/getTableDescription'](
          tableName
        );

      // Processar dados de forma segura
      const processedPhoneColumns = Array.isArray(phoneColumnsData)
        ? phoneColumnsData
        : [];
      const processedNameColumns = Array.isArray(nameColumnsData)
        ? nameColumnsData
        : [];
      const processedAllColumns = Array.isArray(description)
        ? description.map(column => ({ column_name: column.column_name }))
        : [];

      // ATRIBUIÇÃO DIRETA (como na versão original)
      phoneColumns.value = processedPhoneColumns;
      nameColumns.value = processedNameColumns;
      allColumns.value = processedAllColumns;

      // Salvar no cache
      await store.dispatch('campaigns/setCachedTableColumns', {
        tableName,
        phoneColumns: processedPhoneColumns,
        nameColumns: processedNameColumns,
        allColumns: processedAllColumns,
      });
    } catch (error) {
      console.error('❌ [fetchTableColumns] Erro ao carregar colunas:', error);
      phoneColumns.value = [];
      nameColumns.value = [];
      allColumns.value = [];
    }
  };

  const fetchColumns = async (tableName, filterPhoneColumns = false) => {
    try {
      await store.dispatch('externalDatabaseConnection/describeTable', {
        tableName,
        filterPhoneColumns,
      });
      const description =
        store.getters['externalDatabaseConnection/getTableDescription'](
          tableName
        );

      columns.value = Array.isArray(description)
        ? description.map(column => ({ column_name: column.column_name }))
        : [];
    } catch (error) {
      console.error('❌ [fetchColumns] Erro ao consultar colunas:', error);
      columns.value = [];
    }
  };

  // Busca o limite NO CANAL (sem ir na Meta: a sondagem diária de saúde mantém
  // o tier fresco). Antes isto só lia o store, preenchido apenas pela tela
  // antiga de campanhas — no fluxo atual vinha vazio e a prévia mostrava
  // "Ilimitado" com o canal travado em 1.000 (caso Lumen, 2026-10-21).
  // O endpoint só existe pra API oficial (422 `whatsapp_channel_not_cloud`):
  // este hook também serve a tela do Baileys, que segue lendo o store.
  //
  // O store (`campaigns/getMessageLimit`) SÓ vale pra caixa que sabidamente
  // não é oficial. Qualquer outra coisa que impeça a leitura — campanha sem
  // caixa, caixa que não existe nem depois de recarregar a lista, busca que
  // falhou ou voltou vazia — vira `whatsappLimitError`, e a prévia diz "Não foi
  // possível ler o limite" em vez de "Ilimitado" (ver previewLimit.js).
  const resolveProvider = async inboxId => {
    if (!inboxId) throw new Error('campanha sem caixa selecionada');

    const findInbox = () => store.getters['inboxes/getInbox'](inboxId);
    // F5 na tela de lista: a lista de caixas ainda não foi carregada.
    if (!findInbox()?.id) await store.dispatch('inboxes/get');
    const inbox = findInbox();
    if (!inbox?.id) throw new Error(`caixa ${inboxId} não encontrada`);
    return inbox.provider;
  };

  const fetchWhatsappLimit = async () => {
    isFetchingWhatsappLimit.value = true;
    whatsappLimitError.value = null;
    try {
      const inboxId = store.getters['campaigns/getCampaignData']?.inboxId;
      const provider = await resolveProvider(inboxId);

      if (!OFFICIAL_API_PROVIDERS.includes(provider)) {
        whatsappMessageLimit.value =
          store.getters['campaigns/getMessageLimit'] || {};
        return;
      }

      const limit = await store.dispatch(
        'externalDatabaseConnection/fetchWhatsappMessageLimit',
        inboxId
      );
      if (!limit) throw new Error('resposta vazia do limite');
      whatsappMessageLimit.value = limit;
    } catch (error) {
      console.error(
        '❌ [fetchWhatsappLimit] Erro ao buscar limite do WhatsApp:',
        error
      );
      whatsappMessageLimit.value = {};
      whatsappLimitError.value = error;
    } finally {
      isFetchingWhatsappLimit.value = false;
    }
  };

  const fetchTablePreview = async (tableName, filters) => {
    if (!tableName) {
      console.warn(
        '⚠️ [fetchTablePreview] Nenhuma tabela selecionada para pré-visualização'
      );
      return;
    }

    isTableLoading.value = true;

    try {
      const safeFilters = Array.isArray(filters) ? filters : [];

      const response = await store.dispatch(
        'externalDatabaseConnection/queryTable',
        {
          tableName,
          options: {
            filters: safeFilters.map(filter => ({
              attribute_key: filter.attributeKey,
              filter_operator: filter.filterOperator,
              query_operator: filter.queryOperator,
              group_index: filter.groupIndex,
              values: filter.values,
            })),
            columns: ['*'],
            maxRecords: 50,
            include_count: safeFilters.length > 0,
          },
        }
      );

      // ✅ NOVA ESTRUTURA do Jbuilder: response.data (array de rows)
      tableData.value = Array.isArray(response?.data) ? response.data : [];
      totalCount.value =
        response?.metadata?.total_count ||
        response?.metadata?.showing_count ||
        response?.data?.length ||
        0;
      generatedSql.value = response?.metadata?.generated_sql || null; // ✅ Armazenar SQL gerada
    } catch (error) {
      console.error('[fetchTablePreview] Erro:', error);
      tableData.value = [];
      totalCount.value = 0;
    } finally {
      isTableLoading.value = false;
    }
  };

  return {
    // State
    tables,
    phoneColumns,
    nameColumns,
    allColumns,
    columns,
    tableData,
    totalCount,
    generatedSql, // ✅ NOVO: SQL gerada
    isTableLoading,
    whatsappMessageLimit,
    isFetchingWhatsappLimit,
    whatsappLimitError,

    // Computed options
    tableOptions,
    phoneColumnOptions,
    nameColumnOptions,
    allColumnOptions,
    columnOptions,

    // Functions
    fetchTables,
    fetchTableColumns,
    fetchColumns,
    fetchWhatsappLimit,
    fetchTablePreview,
  };
}
